// Контроллер прототипа + настоящий API: вход, регистрация, онбординг, выход, загрузка, профиль.
// Сгенерированный controller.gen.js не трогаем — переопределяем только нужные методы.
import {
  Component,
  IC,
  OB_TPL,
  OB_TUTOR,
  PROFILE_DEF,
  SPACE_COLORS,
  clone,
  setMembers,
  strip,
} from './generated/controller.gen.js';
import {
  applyGraphDiff,
  findMentions,
  notificationAction,
  notificationWho,
  YDOC_BLOCKS,
  yToGraph,
} from '@socrat/shared';
import { generateKeyBetween } from 'fractional-indexing';
import { CollabHub, LOCAL } from './collab-client';
import { FEAT, setFeatures } from './features';
import { contentIn, contentOut, kindOut } from './live-api';
import { blockRefs, blocksToText } from './md-blocks';
import { mergeDoc, mergeGraph } from './merge';
import { overlay } from './overlay-store';

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Роли: у нас owner/admin/moderator/member/viewer, в дизайне — owner/admin/member/guest.
const TO_PROTO_ROLE = { owner: 'owner', admin: 'admin', moderator: 'member', member: 'member', viewer: 'guest' };
const FROM_PROTO_ROLE = { admin: 'admin', member: 'member', guest: 'viewer' };
const ROLE_LABEL = { owner: 'Владелец', admin: 'Админ', moderator: 'Модератор', member: 'Участник', viewer: 'Гость' };
const AVATAR = [
  ['#ffe4cf', '#b4501f'],
  ['#e9e3fb', '#5b3fb5'],
  ['#dcefe4', '#1f6d45'],
  ['#e3e9fd', '#3550c4'],
  ['#fdf0c4', '#8a6200'],
  ['#fde4ec', '#a3245a'],
];
const ini = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
/** «12 мин назад» — как в прототипе. */
const agoText = (t) => {
  const m = Math.round((Date.now() - t) / 60000);
  if (m < 1) return 'только что';
  if (m < 60) return m + ' мин назад';
  if (m < 1440) return Math.round(m / 60) + ' ч назад';
  return new Date(t).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};
const seg = (on) => ({
  bg: on ? 'hsl(var(--background))' : 'transparent',
  color: on ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
  sh: on ? '0 1px 3px rgba(16,24,40,.08)' : 'none',
});

/** Учебный проект без упоминаний функций в разработке (ADR-017): AI-подсказок, задач, тарифов. */
function tutorTree() {
  let json = JSON.stringify(OB_TUTOR.tree);
  if (!FEAT.ai) json = json.replace(/\\n\\n> AI тихо подсказывает связи[^\\]*?в графе\./, '');
  if (!FEAT.tasks) json = json.replace('F — рамка, K — задача', 'F — рамка, I — картинка');
  if (!FEAT.billing) json = json.replace('свои участники и тариф', 'свои участники');
  return JSON.parse(json);
}

export class LiveController extends Component {
  get live() {
    return this.props.apiMode === 'live';
  }

  // ---------- подписка для оверлеев (Overlays.tsx) ----------

  _subs = new Set();
  _ver = 0;
  subscribe = (fn) => {
    this._subs.add(fn);
    return () => this._subs.delete(fn);
  };
  version = () => this._ver;
  setState(update, cb) {
    super.setState(update, () => {
      this._ver++;
      this._subs.forEach((f) => f());
      cb?.();
    });
  }

  // ---------- права из API вместо твика role ----------

  /** Редактировать можно, если право на открытый материал (или на проект) — edit/manage. */
  get canEdit() {
    if (!this.live) return super.canEdit;
    const s = this.state;
    const r = s.openId ? this.res(s.openId) : null;
    const perm = r?.permission ?? s.snap?.project?.permission;
    return perm === 'edit' || perm === 'manage';
  }

  duplicateRes(id) {
    this.api.duplicate(id).then(
      (copy) =>
        this.refresh().then(() =>
          overlay.snack(`Создана копия «${copy.title}»`, {
            label: 'Открыть',
            run: () => {
              overlay.set({ snack: null });
              this.open(copy.id);
            },
          }),
        ),
      (e) => overlay.snack(e.code === 'forbidden' || e.status === 403 ? 'Недостаточно прав' : e.message),
    );
  }

  /** Строки дерева: id материала (для «⋯» и правого клика) — тем же порядком, что строит прототип. */
  decorateRows(v) {
    const s = this.state,
      snap = s.snap;
    if (!snap || !Array.isArray(v.rows) || !v.rows.length) return;
    const rs = snap.resources;
    const q = (s.q || '').trim().toLowerCase();
    let ids = [];
    if (q)
      ids = rs
        .filter((r) => r.title.toLowerCase().includes(q) || strip(r.content?.text).toLowerCase().includes(q))
        .map((r) => r.id);
    else {
      rs.filter((r) => r.kind === 'folder' && !r.parent_id).forEach((f) => {
        ids.push(f.id);
        if (!s.collapsed[f.id])
          rs.filter((r) => r.parent_id === f.id && r.kind !== 'task').forEach((c) => ids.push(c.id));
      });
      rs.filter((r) => !r.parent_id && r.kind !== 'folder' && r.kind !== 'task').forEach((r) => ids.push(r.id));
    }
    if (ids.length !== v.rows.length) return; // порядок дизайна изменился — меню без привязки лучше, чем к чужому файлу
    v.rows = v.rows.map((row, i) => ({
      ...row,
      dcKey: ids[i],
      onContextMenu: (e) => {
        e.preventDefault();
        overlay.openMenu(ids[i], e.clientX, e.clientY);
      },
    }));
  }

  // ---------- адрес страницы: ?p=<проект>&r=<файл>[&c=<ветка обсуждения>] ----------

  syncUrl() {
    const s = this.state;
    // До входа pid — заглушка прототипа ('p1'); пока ссылка из адреса не открыта, адрес тоже не трогаем.
    if (!this._booted || !s.snap || s.au || location.pathname !== '/' || this._linkR) return;
    const want = '?p=' + s.pid + (s.openId ? '&r=' + s.openId : '');
    if (location.search !== want) history.replaceState(null, '', '/' + want);
  }

  /**
   * Ссылка из адреса: открыть проект и файл после загрузки. Читаем один раз при создании —
   * до входа рендер с сохранённым pid уже вызывает syncUrl, и адрес успел бы перезаписаться.
   */
  _link = (() => {
    const q = new URLSearchParams(location.search);
    return { p: q.get('p'), r: q.get('r'), c: q.get('c') };
  })();
  _linkR = this._link.r;

  openDeepLink(r) {
    this._linkR = null;
    if (r && this.state.snap?.resources.some((x) => x.id === r)) {
      this.open(r);
      // ссылка из письма об упоминании (&c=ветка) — сразу с открытым обсуждением
      if (this._link.c) this.setState({ mwOpen: true, mwPeople: false, inviting: false });
    } else this.syncUrl();
  }

  /** Сессия прототипа ({ name, email, onboarded }) — из /me, а не из localStorage. */
  sess() {
    if (!this.live) return super.sess();
    return this._me ? { name: this._me.name, email: this._me.email, onboarded: true } : null;
  }

  setSess(v) {
    if (!this.live) return super.setSess(v);
    if (!v) {
      this._me = null;
      return;
    }
    const name = (v.name || '').trim();
    if (this._me && name && name !== this._me.name) {
      this._me = { ...this._me, name };
      this.api?.patchMe({ name: name.slice(0, 80) }).catch(() => {});
    }
  }

  /** Профиль из /me — в настройки и карточку в навигации. */
  applyMe(me) {
    this._me = me;
    if ('admin' in me) this._admin = !!me.admin; // только /me знает, что запрос через VPN администратора
    this.collab?.setUser(me.id, me.name);
    const ui = this.state.ui;
    super.setUi({
      profile: {
        ...(ui.profile || PROFILE_DEF),
        name: me.name,
        email: me.email,
        role: me.title || '',
        tz: me.timezone || 'UTC',
        notif: {
          mentions: me.notificationPrefs?.mentions ?? true,
          digest: me.notificationPrefs?.digest ?? true,
          ai: me.notificationPrefs?.product ?? false,
        },
      },
    });
  }

  /** Изменения профиля в настройках → PATCH /me (с задержкой). */
  setUi(patch) {
    super.setUi(patch);
    if (!this.live || !patch.profile || !this._me) return;
    const p = patch.profile;
    const body = {};
    if (p.name && p.name.trim() && p.name.trim() !== this._me.name) body.name = p.name.trim().slice(0, 80);
    if (p.role !== undefined && p.role !== (this._me.title || '')) body.title = String(p.role).slice(0, 120);
    if (p.tz && p.tz !== this._me.timezone) body.timezone = p.tz;
    if (p.notif)
      body.notificationPrefs = { mentions: !!p.notif.mentions, digest: !!p.notif.digest, product: !!p.notif.ai };
    if (!Object.keys(body).length) return;
    clearTimeout(this._meT);
    this._meT = setTimeout(() => {
      this.api
        .patchMe(body)
        .then((me) => (this._me = me))
        .catch((e) => this.handleErr(e));
    }, 600);
  }

  auInit() {
    if (!this.live) return super.auInit();
    this.setState({ au: null, auName: '', auEmail: '', auPass: '', auErr: {}, ob: this.obNew(null) });
    // api ещё может грузиться: сессию проверяем прямым запросом.
    import('./live-api').then(async ({ http }) => {
      // что включено на сервере (ADR-017) — до входа: скрытое не должно мелькнуть
      await http('/config').then(
        (cfg) => setFeatures(cfg.features),
        () => setFeatures([]),
      );
      await this.handleEmailLink(http);
      http('/me').then(
        (me) => this.signedIn(me),
        (e) => {
          // Не сбрасываем выбор, если пользователь уже переключил вкладку, пока шёл запрос (медленная сеть).
          // Пришёл по приглашению — скорее всего, аккаунта ещё нет: сразу регистрация.
          if (e.status === 401)
            this.setState((s) => (s.au == null ? { au: this._fromInvite ? 'signup' : 'login' } : null));
          else this.setState({ error: e.message });
        },
      );
    });
  }

  /** Острова и редакторы — только после входа: экран входа и онбординга не грузит сотни КБ библиотек. */
  loadIslands() {
    if (!this.live) return super.loadIslands();
    if (!this._authed) {
      this._islandsWanted = true;
      return;
    }
    if (this._islandsStarted) return;
    this._islandsStarted = true;
    super.loadIslands();
  }

  signedIn(me) {
    this.applyMe(me);
    // Заявка ещё не одобрена (white list, ADR-017): поверх — экран ожидания (Overlays), приложение не грузим.
    // Приглашение примет после одобрения — токен остаётся в sessionStorage.
    if (me.access && me.access !== 'approved') {
      this.setState({ au: null, empty: false });
      return;
    }
    this._authed = true;
    if (me.admin) this.adminHint();
    else if (!('admin' in me))
      import('./live-api').then(({ http }) =>
        http('/me').then(
          (m) => {
            this._admin = !!m.admin;
            if (m.admin) this.adminHint();
          },
          () => {},
        ),
      );
    if (this._islandsWanted) this.loadIslands();
    const invite = sessionStorage.getItem('socrat.invite');
    if (invite) {
      sessionStorage.removeItem('socrat.invite');
      import('./live-api').then(({ http }) =>
        http('/invitations/accept', 'POST', { token: invite }).then(
          (r) => {
            this.toast(r.joined ? 'Вы в спейсе — добро пожаловать!' : 'Вы уже участник этого спейса');
            this.bootWhenReady();
          },
          (e) => {
            this.toast(
              e.code === 'email_not_verified'
                ? 'Подтвердите почту — ссылка в письме — и откройте приглашение снова'
                : e.code === 'wrong_recipient'
                  ? 'Приглашение отправлено на другую почту'
                  : e.message,
            );
            this.bootWhenReady();
          },
        ),
      );
      return;
    }
    this.bootWhenReady();
  }

  /** Администратор через VPN: новые заявки — подсказкой; ссылка из письма (?admin=access) открывает их сразу. */
  adminHint() {
    const fromMail = new URLSearchParams(location.search).get('admin') === 'access';
    if (fromMail) {
      history.replaceState(null, '', location.pathname);
      setTimeout(() => overlay.openDialog('admission'), 800);
      return;
    }
    import('./live-api').then(({ http }) =>
      http('/admin/access?status=pending').then(
        (r) =>
          r.pending &&
          setTimeout(
            () =>
              overlay.snack('Новых заявок на доступ: ' + r.pending, {
                label: 'Открыть',
                run: () => overlay.openDialog('admission'),
              }),
            1200,
          ),
        () => {},
      ),
    );
  }

  /** Загрузка после входа: если базовый контроллер уже пытался загрузиться до входа — повторяем; иначе он сам загрузится, когда подгрузит api. */
  bootWhenReady() {
    if (this._pendingBoot === undefined || !this.api) return;
    const pref = this._pendingBoot === true ? undefined : this._pendingBoot;
    this._pendingBoot = undefined;
    this.boot(pref);
  }

  /**
   * Ссылки из писем: токен во фрагменте (#token=…), его убираем из адреса сразу.
   * /auth/magic — вход, /auth/verify-email — подтверждение, /invite — приглашение (после входа),
   * /auth/reset — новый пароль (экран ResetPassword, см. LegacyApp).
   */
  async handleEmailLink(http) {
    const token = new URLSearchParams(location.hash.slice(1)).get('token');
    const path = location.pathname;
    if (!token || path === '/auth/reset') return;
    history.replaceState(null, '', '/');
    try {
      if (path === '/auth/magic') {
        const r = await http('/auth/magic-link/verify', 'POST', { token });
        if (r.redirect && r.redirect !== '/') history.replaceState(null, '', r.redirect);
      } else if (path === '/auth/verify-email') {
        await http('/auth/verify-email', 'POST', { token });
        this.toast('Почта подтверждена');
      } else if (path === '/invite') {
        const p = await http('/invitations/preview', 'POST', { token });
        sessionStorage.setItem('socrat.invite', token);
        this._fromInvite = true;
        this.toast('Приглашение в «' + p.spaceName + '» — войдите или зарегистрируйтесь, чтобы принять');
      }
    } catch (e) {
      this.toast(e.code === 'invalid_token' || e.status === 404 ? 'Ссылка устарела или уже использована' : e.message);
    }
  }

  obNew(ss) {
    // Сайты появятся позже — по умолчанию не создаём.
    return { ...super.obNew(ss), site: false };
  }

  async auSubmit() {
    if (!this.live) return super.auSubmit();
    const s = this.state;
    const mode = s.au;
    const err = {};
    const em = (s.auEmail || '').trim();
    const pw = s.auPass || '';
    const nm = (s.auName || '').trim();
    if (!EMAIL_RE.test(em)) err.email = 'Введите почту в формате name@company.com';
    if (mode !== 'forgot' && pw.length < 8) err.pass = 'Пароль — не меньше 8 символов';
    if (mode === 'signup' && nm.length < 2) err.name = 'Как к вам обращаться?';
    if (mode === 'signup' && !s.auTerms) err.terms = 'Нужно согласие с условиями';
    this.setState({ auErr: err });
    if (Object.keys(err).length) return;
    this.setState({ auBusy: true });
    try {
      if (mode === 'forgot') {
        await this.api.resetRequest(em);
        this.setState({ auBusy: false, au: 'sent' });
        return;
      }
      const { user } =
        mode === 'signup'
          ? await this.api.register({ email: em, name: nm, password: pw })
          : await this.api.login(em, pw);
      this.setState({ auBusy: false, auPass: '', au: null, ob: this.obNew({ name: user.name }) });
      // Пустой спейс → boot сам откроет онбординг; иначе — приложение.
      this.signedIn(user);
      if (mode === 'login') this.toast('С возвращением, ' + user.name.split(' ')[0]);
    } catch (e) {
      this.setState({ auBusy: false });
      this.authError(e);
    }
  }

  authError(e) {
    const f = e.fields?.fieldErrors || {};
    if (e.code === 'invalid_credentials') this.setState({ auErr: { pass: 'Неверная почта или пароль' } });
    else if (e.code === 'email_taken') this.setState({ auErr: { email: 'Эта почта уже зарегистрирована — войдите' } });
    else if (e.code === 'account_inactive') this.setState({ auErr: { email: 'Аккаунт заблокирован или удалён' } });
    else if (e.code === 'mail_disabled') this.toast('Восстановление по почте пока недоступно — напишите в поддержку');
    else if (e.code === 'validation')
      this.setState({ auErr: { email: f.email?.[0], pass: f.password?.[0], name: f.name?.[0] } });
    else if (e.status === 429)
      this.toast('Слишком много попыток. Попробуйте через ' + Math.ceil((+e.retryAfter || 60) / 60) + ' мин.');
    else this.toast(e.message || 'Не получилось, попробуйте ещё раз');
  }

  auOauth(p) {
    if (!this.live) return super.auOauth(p);
    this.api.config().then(
      (cfg) => {
        if (cfg.auth?.oauth?.[p]) location.href = '/api/auth/oauth/' + p + '?redirect=/';
        else this.toast('Вход через ' + (p === 'google' ? 'Google' : 'GitHub') + ' появится позже');
      },
      (e) => this.handleErr(e),
    );
  }

  logout() {
    if (!this.live) return super.logout();
    const done = () => {
      this._me = null;
      this._admin = false;
      this._authed = false;
      this._collab?.destroy();
      this._collab = null;
      this._cmLive = null;
      this._agent = null;
      this._ib = null;
      super.setUi({ profile: PROFILE_DEF });
      this.setState({ spaces: [], projects: [], snap: null, pid: null, sid: null, empty: false });
      super.logout();
    };
    this.api.logout().then(done, done);
  }

  /**
   * Онбординг: у нас домашний спейс создаётся при регистрации — переименовываем и красим его,
   * а не создаём новый. Сайт — позже; приглашения — настоящие (письмо, если почта настроена).
   */
  async obCreate() {
    if (!this.live) return super.obCreate();
    const ob = this.state.ob;
    const api = this.api;
    const pause = () => wait(300);
    const tick = (i) => this.setState((s) => ({ ob: { ...s.ob, done: [...s.ob.done, i] } }));
    const mk = (k, parent_id) => ({
      kind: k.kind,
      title: k.title,
      content: clone(k.content),
      parent_id,
      personal: !!k.personal,
    });
    const build = async (pid, tree) => {
      const ids = {};
      const notes = [];
      for (const f of tree) {
        if (f.kids) {
          const { id: fid } = await api.createResource(pid, { kind: 'folder', title: f.title, personal: !!f.personal });
          for (const k of f.kids) {
            const r = await api.createResource(pid, mk(k, fid));
            ids[k.title] = r;
            if (k.kind === 'note') notes.push([r, k]);
          }
        } else {
          const r = await api.createResource(pid, mk(f, null));
          ids[f.title] = r;
          if (f.kind === 'note') notes.push([r, f]);
        }
      }
      // [[Название]] в тексте → links (сервер по ним строит связи).
      for (const [r, k] of notes) {
        const L = [...(k.content.text || '').matchAll(/\[\[([^\]]+)\]\]/g)].map((m) => ids[m[1]]?.id).filter(Boolean);
        if (L.length)
          await api
            .patchResource(r.id, { version: r.version, content: { ...k.content, links: [...new Set(L)] } })
            .catch(() => {});
      }
    };
    try {
      this.obSet({ step: 4, done: [] });
      const spaces = await api.getSpaces();
      const home = spaces.find((x) => x.is_home) || spaces[0];
      await api.patchSpace(home.id, { name: ob.space.trim().slice(0, 160), color: ob.color, slugFromName: true });
      if (ob.name.trim() && ob.name.trim() !== this._me?.name) {
        this.applyMe(await api.patchMe({ name: ob.name.trim().slice(0, 80) }));
      }
      await pause();
      tick(0);
      const { id: pid } = await api.createProject(home.id, {
        name: OB_TUTOR.name,
        description: OB_TUTOR.desc,
        color: ob.color,
        icon: 'lucide:graduation-cap',
        template: 'tutorial',
      });
      await build(pid, tutorTree());
      if (ob.tpl !== 'none') {
        const T = OB_TPL[ob.tpl];
        const { id: p2 } = await api.createProject(home.id, {
          name: T.name,
          description: T.desc,
          color: SPACE_COLORS[(SPACE_COLORS.indexOf(ob.color) + 3) % SPACE_COLORS.length],
          icon: 'lucide:folder',
          template: ob.tpl,
        });
        await build(p2, T.tree);
      }
      await pause();
      tick(1);
      await pause();
      tick(2);
      const ems = ob.invites
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter((x) => EMAIL_RE.test(x));
      for (const email of ems) {
        await api.invite(home.id, { kind: 'email', email, role: 'member' }).catch(() => {});
      }
      await pause();
      tick(3);
      this.setState((s) => ({ ob: { ...s.ob, ready: true, sid: home.id, pid } }));
    } catch (e) {
      this.toast(e.message || 'Не удалось подготовить спейс');
      this.obSet({ step: 3 });
    }
  }

  // ---------- участники и приглашения ----------

  /** Участники и активные приглашения спейса (кэш в state.memData[sid]). */
  loadMembers(sid, force) {
    // до загрузки в состоянии id спейса-заглушки прототипа ('s2') — сервер его не знает
    if (
      !sid ||
      !this.api ||
      !/^[0-9a-f-]{36}$/i.test(sid) ||
      (!force && (this.state.memData?.[sid] || this._memLoading?.[sid]))
    )
      return;
    this._memLoading = { ...(this._memLoading || {}), [sid]: true };
    Promise.all([this.api.members(sid), this.api.invitations(sid).catch(() => [])]).then(
      ([list, invites]) => {
        this._memLoading[sid] = false;
        this.setState((st) => ({ memData: { ...(st.memData || {}), [sid]: { list, invites } } }));
      },
      (e) => {
        this._memLoading[sid] = false;
        this.handleErr(e);
      },
    );
  }

  /** Участник API → формат прототипа [ini, name, role, online, bg, fg, status]. */
  people(sid) {
    const list = this.state.memData?.[sid]?.list || [];
    const meId = this._me?.id;
    return list.map((m, i) => {
      const [bg, fg] = AVATAR[i % AVATAR.length];
      const isMe = m.userId === meId;
      return [ini(m.name), m.name, TO_PROTO_ROLE[m.role] || 'member', isMe ? 1 : 0, bg, fg, isMe ? '' : 'не в сети', m];
    });
  }

  myRole(sid) {
    return (this.state.spaces || []).find((x) => x.id === sid)?.role;
  }

  /** Пригласить по почте; роль из дизайна (member/guest/admin) → наша. */
  inviteEmails(sid, emails, protoRole) {
    const role = FROM_PROTO_ROLE[protoRole] || 'member';
    return Promise.all(emails.map((email) => this.api.invite(sid, { kind: 'email', email, role }))).then(
      (rs) => {
        this.loadMembers(sid, true);
        const sent = rs.filter((r) => r.emailSent).length;
        this.toast(
          (emails.length > 1 ? 'Приглашения созданы: ' + emails.length : 'Приглашение для ' + emails[0]) +
            (sent ? '' : ' · письма пока не отправляются — поделитесь ссылкой'),
        );
      },
      (e) =>
        this.toast(
          e.code === 'already_member'
            ? 'Этот человек уже в спейсе'
            : e.code === 'role_forbidden'
              ? 'Нельзя пригласить с этой ролью'
              : e.message,
        ),
    );
  }

  /** Ссылка-приглашение: создаётся (или перевыпускается) по запросу и копируется. */
  copyInviteLink(sid, protoRole, regen) {
    const role = FROM_PROTO_ROLE[protoRole] || 'member';
    this.api.invite(sid, { kind: 'link', role }).then(
      (r) => {
        this.setState((st) => ({ joinUrls: { ...(st.joinUrls || {}), [sid]: r.url } }));
        this.copy(
          r.url,
          regen
            ? 'Новая ссылка скопирована, старая больше не работает'
            : 'Ссылка-приглашение скопирована (действует 7 дней)',
        );
      },
      (e) =>
        this.toast(e.code === 'role_forbidden' || e.status === 403 ? 'Приглашать могут модераторы и выше' : e.message),
    );
  }

  /** Панель участников (копия membersVals прототипа с данными из API). */
  membersVals(s, ui, canEdit) {
    if (!this.live) return super.membersVals(s, ui, canEdit);
    this.loadMembers(s.sid);
    const base = super.membersVals(s, ui, canEdit);
    const open = s.mwOpen,
      dark = ui.theme === 'dark';
    const ROLE = {
      owner: ['Владелец', 'crown', '#b4501f', '#fff1e6', '#ffd6b3'],
      admin: ['Админ', 'shield', '#3550c4', '#eef2ff', '#cdd7ff'],
      member: ['Участник', 'user-round', '#1f6d45', '#ecf8f1', '#c3e8d2'],
      guest: ['Гость', 'eye', '#5b3fb5', '#f4efff', '#ddd0ff'],
    };
    const PEOPLE = this.people(s.sid);
    this._people = PEOPLE;
    if (!PEOPLE.length) return base;
    const q = (s.mwQ || '').trim().toLowerCase(),
      online = PEOPLE.filter((p) => p[3]).length;
    const list = PEOPLE.filter((p) => !q || p[1].toLowerCase().includes(q) || ROLE[p[2]][0].toLowerCase().includes(q));
    const myRole = this.myRole(s.sid);
    const canInvite = ['owner', 'admin', 'moderator'].includes(myRole);
    const pl = (n) => {
      const a = n % 100,
        b = n % 10;
      return a > 10 && a < 20 ? 'участников' : b === 1 ? 'участник' : b > 1 && b < 5 ? 'участника' : 'участников';
    };
    const send = () => {
      if (s.invMode === 'link') return this.copyInviteLink(s.sid, s.invRole);
      const ems = (s.invEmail || '')
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      if (!ems.length || ems.some((em) => !EMAIL_RE.test(em))) return this.toast('Проверьте адреса почты');
      this.setState({ inviting: false, invEmail: '' });
      this.inviteEmails(s.sid, ems, s.invRole);
    };
    const link = this.state.joinUrls?.[s.sid];
    return {
      ...base,
      dock: PEOPLE.map((p) => ({
        ini: p[0],
        name: p[1] + (p[3] ? ' · в сети' : ''),
        online: !!p[3],
        bg: p[4],
        fg: p[5],
        onClick: (e) => {
          e.stopPropagation();
          this.setState({ mwOpen: true, mwPeople: true, inviting: false });
        },
      })),
      onlineN: String(online),
      onlineText: online + ' в сети',
      totalText: PEOPLE.length + ' ' + pl(PEOPLE.length),
      sub: open ? `${PEOPLE.length} в спейсе · ${online} в сети` : `${online} в сети`,
      top: PEOPLE.slice(0, 3).map((p) => ({ ini: p[0], name: p[1], bg: p[4], fg: p[5] })),
      more: '+' + Math.max(0, PEOPLE.length - 3),
      canInvite,
      sendInvite: send,
      onInvKey: (e) => {
        if (e.key === 'Enter') send();
      },
      link: link ? link.replace(/^https?:\/\//, '').slice(0, 48) + '…' : 'Ссылка создастся при копировании',
      regenLink: () => this.copyInviteLink(s.sid, s.invRole, true),
      copyLink: () => this.copyInviteLink(s.sid, s.invRole),
      invRoles: [
        ['member', 'Участник'],
        ['guest', 'Гость'],
        ['admin', 'Админ'],
      ]
        .filter(([k]) => myRole === 'owner' || (myRole === 'admin' ? k !== 'admin' : k !== 'admin'))
        .map(([k, label]) => ({
          label,
          ...seg((s.invRole || 'member') === k),
          onClick: () => this.setState({ invRole: k }),
        })),
      empty: !list.length,
      list: list.map((p, i) => {
        const R = ROLE[p[2]];
        return {
          ini: p[0],
          name: p[1],
          online: !!p[3],
          status: p[3] ? 'В сети' : p[6] || 'не в сети',
          stColor: p[3] ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))',
          bg: p[4],
          fg: p[5],
          role: ROLE_LABEL[p[7].role] || R[0],
          rIcon: IC(R[1]),
          rFg: dark ? R[4] : R[2],
          rBg: dark ? R[2] + '33' : R[3],
          rBd: dark ? R[2] + '66' : R[4],
          op: open ? 1 : 0,
          tx: open ? 'none' : 'translate(10px,14px) rotate(1deg)',
          dl: open ? 120 + i * 30 + 'ms' : '0ms',
        };
      }),
    };
  }

  // ---------- настройки спейса ----------

  /** Правка общих полей спейса: сразу локально (как в прототипе), на сервер — с задержкой. */
  saveSpaceSoon(sid, patch) {
    this._spPatch = { ...(this._spPatch || {}), ...patch };
    clearTimeout(this._spT);
    this._spT = setTimeout(() => {
      const body = this._spPatch;
      this._spPatch = null;
      this.api.patchSpace(sid, body).then(
        (sp) => {
          this.setState((st) => ({ spaces: st.spaces.map((x) => (x.id === sid ? { ...x, ...sp } : x)) }));
          // Сохранено — локальные черновики имени/адреса/описания больше не нужны.
          const ui = this.state.ui,
            cfg = { ...(ui.spaceCfg[sid] || {}) };
          delete cfg.name;
          delete cfg.slug;
          delete cfg.desc;
          super.setUi({ spaceCfg: { ...ui.spaceCfg, [sid]: { ...cfg, color: sp.color } } });
        },
        (e) =>
          this.toast(
            e.code === 'slug_taken'
              ? 'Этот адрес уже занят'
              : e.code === 'validation'
                ? 'Проверьте поля: адрес — от 3 символов, латиница и дефис'
                : e.message,
          ),
      );
    }, 700);
  }

  async boot(pref) {
    const first = !this._booted;
    const link = first ? this._link : {};
    const r = await this.bootGuarded(pref ?? (first ? (link.p ?? undefined) : undefined));
    if (this.live && this._authed) {
      this._booted = true;
      this.syncSpaceColors();
      if (first && link.r) {
        // снимок мог ещё грузиться — открываем, когда придёт
        const t0 = Date.now();
        const tryOpen = () =>
          this.state.snap
            ? this.openDeepLink(link.r)
            : Date.now() - t0 < 8000
              ? setTimeout(tryOpen, 150)
              : this.openDeepLink(null);
        tryOpen();
      }
    }
    return r;
  }

  bootGuarded(pref) {
    if (this.live && !this._authed) {
      this._pendingBoot = pref ?? true;
      return Promise.resolve();
    }
    return super.boot(pref);
  }

  /** Цвет спейса в дизайне берётся из локальных настроек — заполняем его с сервера. */
  syncSpaceColors() {
    const ui = this.state.ui;
    const cfg = { ...ui.spaceCfg };
    let changed = false;
    for (const sp of this.state.spaces || []) {
      if (sp.color && (cfg[sp.id] || {}).color !== sp.color) {
        cfg[sp.id] = { ...(cfg[sp.id] || {}), color: sp.color };
        changed = true;
      }
    }
    if (changed) super.setUi({ spaceCfg: cfg });
  }

  liveSpaceModal(v, s) {
    const sx = v.sx;
    const sid = s.setSid || s.sid;
    if (!sx?.isEdit || !sid) return;
    this.loadMembers(sid);
    const myRole = this.myRole(sid);
    const canAdmin = myRole === 'owner' || myRole === 'admin';
    const data = this.state.memData?.[sid];
    const onName = sx.onName,
      onSlug = sx.onSlug,
      onDesc = sx.onDesc;
    sx.onName = (e) => {
      onName(e);
      const n = e.target.value.trim();
      if (n) this.saveSpaceSoon(sid, { name: n.slice(0, 160) });
    };
    sx.onSlug = (e) => {
      onSlug(e);
      const sl = e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .slice(0, 40);
      if (sl.length >= 3) this.saveSpaceSoon(sid, { slug: sl });
    };
    sx.onDesc = (e) => {
      onDesc(e);
      this.saveSpaceSoon(sid, { description: e.target.value.slice(0, 300) });
    };
    sx.colors = sx.colors.map((c) => ({
      ...c,
      onClick: () => {
        c.onClick();
        this.saveSpaceSoon(sid, { color: c.c });
      },
    }));
    const sp = (s.spaces || []).find((x) => x.id === sid);
    if (sp?.slug && !(this.state.ui.spaceCfg[sid] || {}).slug) sx.slug = sp.slug;
    if (sp?.description !== undefined && (this.state.ui.spaceCfg[sid] || {}).desc === undefined)
      sx.desc = sp.description;

    if (data) {
      const meId = this._me?.id;
      const members = data.list.map((m, i) => {
        const [bg, fg] = AVATAR[i % AVATAR.length];
        const isMe = m.userId === meId;
        return {
          ini: ini(m.name),
          name: m.name + (isMe ? ' (вы)' : ''),
          sub: (ROLE_LABEL[m.role] || m.role) + ' · ' + (isMe ? 'в сети' : m.email),
          bg,
          fg,
          role: TO_PROTO_ROLE[m.role] || 'member',
          isOwner: m.role === 'owner',
          editable: canAdmin && m.role !== 'owner' && !isMe && !(myRole === 'admin' && m.role === 'admin'),
          bt: i ? '1px solid hsl(var(--border))' : '0',
          onRole: (e) =>
            this.api.updateMember(sid, m.userId, { role: FROM_PROTO_ROLE[e.target.value] || 'member' }).then(
              () => {
                this.loadMembers(sid, true);
                this.toast(
                  m.name.split(' ')[0] +
                    ': ' +
                    ({ admin: 'Админ', member: 'Участник', guest: 'Гость' }[e.target.value] || ''),
                );
              },
              (err) => this.toast(err.code === 'role_forbidden' ? 'Нельзя назначить эту роль' : err.message),
            ),
          onRemove: () => {
            if (!window.confirm('Убрать ' + m.name + ' из спейса?')) return;
            this.api.removeMember(sid, m.userId).then(
              () => {
                this.loadMembers(sid, true);
                this.toast(m.name + ' больше не в спейсе');
              },
              (err) => this.toast(err.message),
            );
          },
        };
      });
      data.invites
        .filter((iv) => iv.kind === 'email')
        .forEach((iv) =>
          members.push({
            ini: iv.email.slice(0, 2).toUpperCase(),
            name: iv.email,
            sub: (ROLE_LABEL[iv.role] || iv.role) + ' · приглашение отправлено',
            bg: 'hsl(var(--muted))',
            fg: 'hsl(var(--muted-foreground))',
            role: TO_PROTO_ROLE[iv.role] || 'member',
            isOwner: false,
            editable: canAdmin,
            bt: '1px solid hsl(var(--border))',
            onRole: () => this.toast('Роль приглашения меняется так: отзовите его и пригласите заново'),
            onRemove: () =>
              this.api.revokeInvitation(sid, iv.id).then(
                () => {
                  this.loadMembers(sid, true);
                  this.toast('Приглашение отозвано');
                },
                (err) => this.toast(err.message),
              ),
          }),
        );
      sx.members = members;
      sx.usage = sx.usage?.map((u) =>
        u.label === 'Участники' ? { ...u, val: u.val.replace(/^\d+/, String(data.list.length)) } : u,
      );
    }
    sx.invite = () => {
      const em = (s.spInv || '').trim().toLowerCase();
      if (!EMAIL_RE.test(em)) return this.toast('Введите почту в формате name@company.com');
      this.setState({ spInv: '' });
      this.inviteEmails(sid, [em], s.spInvRole || 'member');
    };
    sx.onInvKey = (e) => {
      if (e.key === 'Enter') sx.invite();
    };
    const url = this.state.joinUrls?.[sid];
    sx.joinUrl = url || 'Ссылка создастся при копировании';
    sx.copyJoin = () => this.copyInviteLink(sid, s.spInvRole || 'member');
    sx.leave = () => {
      if (myRole === 'owner') return this.toast('Владелец не может покинуть спейс. Передайте права или удалите спейс');
      this.api.leaveSpace(sid).then(
        () => {
          this.setState({ spaceModal: null });
          this.toast('Вы покинули спейс');
          return this.boot();
        },
        (e) => this.toast(e.message),
      );
    };
    sx.isOwner = canAdmin;
  }

  liveNewSpace(v, s) {
    const sn = v.sn;
    if (!sn) return;
    const n = s.spNew || {};
    const nm = n.name || '';
    const col = sn.color;
    const ems = (n.inv || '')
      .split(/[,;\s]+/)
      .map((x) => x.trim())
      .filter((x) => EMAIL_RE.test(x));
    sn.create = async () => {
      if (nm.trim().length < 2) return this.toast('Назовите спейс');
      if (s.spBusy) return;
      this.setState({ spBusy: true });
      try {
        const { id } = await this.api.createSpace({ name: nm.trim(), color: col });
        const { id: pid } = await this.api.createProject(id, {
          name: (n.proj || 'Первый проект').trim() || 'Первый проект',
          color: col,
          icon: 'lucide:folder',
        });
        await this.api.createResource(pid, {
          kind: 'note',
          title: 'С чего начать',
          content: {
            text:
              '# С чего начать\n\nЭто первый проект спейса «' +
              nm.trim() +
              '». Создайте документ, карту или доску кнопкой «Создать».',
          },
        });
        for (const email of ems) await this.api.invite(id, { kind: 'email', email, role: 'member' }).catch(() => {});
        this.setState({ spaceModal: null, spNew: null, spBusy: false });
        this.toast('Спейс «' + nm.trim() + '» создан' + (ems.length ? ', приглашений: ' + ems.length : ''));
        await this.boot(pid);
      } catch (e) {
        this.setState({ spBusy: false });
        this.toast(e.code === 'space_limit' ? 'Достигнут лимит спейсов' : e.message);
      }
    };
    sn.onKey = (e) => {
      if (e.key === 'Enter') sn.create();
    };
  }

  liveAccount(v) {
    v.signOutAll = () => {
      if (!window.confirm('Завершить все сеансы, кроме текущего?')) return;
      this.api.sessions().then(
        async (list) => {
          const others = list.filter((x) => !x.current);
          for (const x of others) await this.api.revokeSession(x.id).catch(() => {});
          this.toast(others.length ? 'Завершено сеансов: ' + others.length : 'Других сеансов нет');
        },
        (e) => this.handleErr(e),
      );
    };
    v.deleteAccount = () => {
      if (window.prompt('Чтобы удалить аккаунт, введите УДАЛИТЬ') !== 'УДАЛИТЬ') return;
      this.api.deleteMe(this._me?.email || '').then(
        () => {
          this.toast('Аккаунт удалён');
          this._me = null;
          this._authed = false;
          super.logout();
        },
        (e) =>
          this.toast(
            e.code === 'owns_shared_spaces'
              ? 'Сначала передайте владение или удалите спейсы с другими участниками: ' +
                  (e.fields?.spaces || []).map((x) => x.name).join(', ')
              : e.message,
          ),
      );
    };
  }

  // ---------- сохранение: очередь и объединение правок при конфликте версий (409) ----------
  // До совместного редактирования (фаза 4) правки одного файла из двух мест сливаются по id блоков и узлов.
  // Пока идёт слияние или открыт диалог, файл «заморожен»: документ — только чтение, карта копит правки локально.

  _saving = {}; // id → PATCH в полёте
  _queued = {}; // id → отложенное сохранение (последнее)
  _base = {}; // id → версия, от которой идут локальные правки (основа слияния): открытие документа, первая правка карты
  _docOpen = null; // key открытого документа — чтобы запомнить основу при открытии
  _docRev = {}; // id → номер пересоздания редактора документа (после слияния)
  conflicts = {}; // id → { kind, base, mine, theirs, items }

  /** Несохранённые правки или открытый конфликт — браузер переспросит перед закрытием вкладки. */
  onBeforeUnload = (e) => {
    const pending =
      Object.keys(this.conflicts).length ||
      Object.values(this._saving).some(Boolean) ||
      this._collab?.pending ||
      Object.values(this.state.save ?? {}).some((v) => v === 'dirty' || v === 'saving' || v === 'conflict');
    if (!this.live || !pending) return;
    e.preventDefault();
    e.returnValue = '';
  };

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('keydown', this.onKbd);
    this.syncInert();
    // Телефон: мобильной раскладки в дизайне нет — закреплённые панели (файлы, свойства документа) по умолчанию
    // свёрнуты, иначе съедают экран; открываются теми же кнопками.
    if (this.live && window.matchMedia?.('(max-width: 767px)').matches) {
      if (!this.state.ui?.panelHidden) this.setUi({ panelHidden: true });
      this.setState({ notePanel: false });
    }
  }

  componentDidUpdate(prev) {
    super.componentDidUpdate(prev);
    this.syncInert();
    this.syncCollab();
    this.syncEvents();
  }

  /**
   * Доступность: то, что спрятано, не должно получать фокус и попадать в дерево доступности — оболочка под экраном
   * входа и онбординга, закрытая выезжающая навигация, скрытая панель файлов. Прототип прячет их только визуально.
   */
  syncInert() {
    const root = document.querySelector('[data-app-root]');
    if (!root) return;
    const kids = [...root.children];
    const auth = kids.find((k) => k.style.position === 'fixed' && k.style.zIndex === '300');
    kids.forEach((k) => k !== auth && k.toggleAttribute('inert', !!auth));
    if (auth) return;
    root.querySelector(':scope > nav')?.toggleAttribute('inert', !this.state.navOpen);
    const files = root.querySelector(':scope > aside');
    files?.toggleAttribute('inert', files.style.pointerEvents === 'none');
    // Кликабельные div прототипа (строки дерева, карточки, пункты меню) — доступны с клавиатуры
    root.querySelectorAll('[data-ai]:not(button):not(a):not(input):not([data-kbd])').forEach((el) => {
      if (el.style.cursor !== 'pointer') return;
      el.dataset.kbd = '1';
      el.tabIndex = 0;
      if (!el.getAttribute('role')) el.setAttribute('role', 'button');
    });
  }

  /** Enter/пробел нажимают кликабельные div; Shift+F10 и клавиша меню открывают меню файла у строки дерева. */
  onKbd = (e) => {
    const el = e.target;
    if (!(el instanceof HTMLElement) || !el.dataset.kbd || e.defaultPrevented) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      el.click();
    } else if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      el.dispatchEvent(
        new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: r.left + 24, clientY: r.bottom }),
      );
    }
  };

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('keydown', this.onKbd);
    super.componentWillUnmount();
  }

  /**
   * Основа для правок: версия, с которой открыт редактор, или последнее своё сохранение. Не «свежий снимок»:
   * если снимок подтянул чужие правки, а редактор их не показывает, сохранение должно дать 409, а не затереть их.
   */
  base(id) {
    return this._base[id] ?? this.res(id);
  }

  /** PATCH по очереди: следующий — только после ответа на предыдущий (иначе свои же правки дают 409). */
  serialSave(id, run) {
    if (this._saving[id]) {
      this._queued[id] = run;
      return;
    }
    this._saving[id] = true;
    Promise.resolve()
      .then(run)
      .finally(() => {
        this._saving[id] = false;
        const next = this._queued[id];
        delete this._queued[id];
        if (next) this.serialSave(id, next);
      });
  }

  saved(nr) {
    this._base[nr.id] = nr;
    this.replaceRes(nr);
    this.setState((s) => ({ save: { ...s.save, [nr.id]: 'saved' } }));
  }

  docPatch(base, pl) {
    const blocks = pl.blocks ?? [];
    const md = pl.md ?? blocksToText(blocks);
    const links = [...new Set([...(pl.refs ?? blockRefs(blocks)), ...this.extractLinks(md, base.id)])]
      .filter((x) => x !== base.id)
      .slice(0, 500);
    return {
      title: ((pl.title || '').trim() || 'Без названия').slice(0, 160),
      content: { ...base.content, blocks, text: md, links, page: pl.page ?? base.content.page },
    };
  }

  saveDocBlocks(id, pl) {
    if (!this.live) return super.saveDocBlocks(id, pl);
    if (this.collabSession(id)?.synced) return this.saveDocMeta(id, pl); // содержимое — через Yjs
    const c = this.conflicts[id];
    if (c) {
      c.mine = pl; // правки, набранные до «заморозки», — в «мою» версию
      return;
    }
    if (!this.res(id) || !this.canEdit) return;
    this.setState((s) => ({ save: { ...s.save, [id]: 'saving' } }));
    this.serialSave(id, () => {
      if (this.conflicts[id]) return this.saveDocBlocks(id, pl);
      const base = this.base(id);
      return this.api.patchResource(id, { version: base.version, ...this.docPatch(base, pl) }).then(
        (nr) => this.saved(nr),
        (err) => {
          if (err.status === 409) return this.docConflict(id, base, pl);
          this.setState((s) => ({ save: { ...s.save, [id]: 'error' } }));
          this.handleErr(err);
        },
      );
    });
  }

  async docConflict(id, base, pl) {
    const c = (this.conflicts[id] = { kind: 'doc', base, mine: pl, theirs: null, items: [] });
    this.setState((s) => ({ save: { ...s.save, [id]: 'conflict' } })); // редактор — только чтение
    try {
      // отложенные сохранения островка (до 0,9 с после последней правки) успевают попасть в c.mine
      const [latest] = await Promise.all([this.api.getResource(id), wait(1000)]);
      c.theirs = latest;
    } catch (e) {
      delete this.conflicts[id];
      return this.handleErr(e);
    }
    const m = this.mergeDocNow(c);
    if (!m.conflicts.length) return this.resolveConflict(id, 'merge');
    c.items = m.conflicts;
    overlay.openDialog('conflict', id);
  }

  mergeDocNow(c) {
    const doc = (title, content) => ({ title: title ?? '', blocks: content?.blocks ?? [], page: content?.page ?? {} });
    const mineTitle = ((c.mine.title || '').trim() || 'Без названия').slice(0, 160);
    return mergeDoc(
      doc(c.base.title, c.base.content),
      doc(mineTitle, { blocks: c.mine.blocks, page: c.mine.page ?? c.base.content.page }),
      doc(c.theirs.title, c.theirs.content),
    );
  }

  saveMap(id) {
    if (!this.live) return super.saveMap(id);
    const cs = this.collabSession(id);
    if (cs?.synced) {
      // своя разница с тем, что было на экране, — в Yjs; положения автораскладки не отправляем (у всех свои)
      const r = this.res(id),
        kind = kindOut('mindmap', r?.content);
      const prev = this._prevGraph[id] ?? yToGraph(cs.doc);
      let next = contentOut(kind, this.mapContent(id));
      if (this.mapLayout(next)) {
        const pos = new Map((prev.nodes ?? []).map((n) => [n.id, n.position]));
        next = { ...next, nodes: next.nodes.map((n) => (pos.has(n.id) ? { ...n, position: pos.get(n.id) } : n)) };
      }
      applyGraphDiff(cs.doc, prev, next, LOCAL);
      this._prevGraph[id] = next;
      this.setState((st) => ({ save: { ...st.save, [id]: 'saved' } }));
      return;
    }
    if (!this.res(id) || this.conflicts[id]) return; // при конфликте правки копятся в state.maps
    this._base[id] ??= this.res(id); // карта: основа — версия до первой локальной правки
    this.setState((s) => ({ save: { ...s.save, [id]: 'saving' } }));
    this.serialSave(id, () => {
      if (this.conflicts[id]) return;
      const base = this.base(id);
      return this.api.patchResource(id, { version: base.version, content: this.mapContent(id) }).then(
        (nr) => this.saved(nr),
        (err) => {
          if (err.status === 409) return this.mapConflict(id, base);
          this.setState((s) => ({ save: { ...s.save, [id]: 'error' } }));
          this.handleErr(err);
        },
      );
    });
  }

  /** Содержимое карты/доски для сохранения — как в saveMap прототипа (положения из раскладки). */
  mapContent(id) {
    const c = clone(this.state.maps[id] ?? this.res(id).content);
    if (c.style?.kind !== 'board')
      c.nodes.forEach((n) => {
        const p = c.style?.structure === 'free' ? n.position : this.mapPos[n.id];
        if (p) n.position = { x: Math.round(p.x), y: Math.round(p.y) };
        if (n.data.refId) n.data.label = '';
      });
    return c;
  }

  /** Карта или доска без несохранённых правок открывается из свежего снимка (локальная копия прототипа живёт долго). */
  open(id) {
    const r = this.live ? this.res(id) : null;
    if (r?.kind === 'task' && !FEAT.tasks) return this.toast('Задачи появятся в следующих версиях');
    if (r && (r.kind === 'mindmap' || r.kind === 'board')) {
      const st = this.state.save?.[id];
      const busy = this._saving[id] || this.conflicts[id] || st === 'dirty' || st === 'saving' || st === 'conflict';
      if (!busy) {
        this._base[id] = r;
        this.setState((s) => ({ maps: { ...s.maps, [id]: undefined } })); // до open прототипа — он создаст копию заново
      }
    }
    return super.open(id);
  }

  /**
   * Задача: поля атомарные (статус, приоритет, исполнитель, срок) — по очереди, при 409 свои поля
   * накладываем на свежую версию. Быстрые клики по статусу и приоритету больше не дают ложный конфликт.
   */
  patchTask(id, patch) {
    if (!this.live) return super.patchTask(id, patch);
    const r = this.res(id);
    if (!r || !this.canEdit) return;
    // в другую колонку — в её конец (в дизайне карточку бросают на колонку, а не между карточками)
    if (patch.status && patch.status !== r.content.status && !('rank' in patch))
      patch = { ...patch, rank: this.rankAfter(patch.status, id) };
    this.replaceRes({ ...r, content: { ...r.content, ...patch } }); // сразу на экране
    const send = (base) =>
      this.api.patchResource(id, { version: base.version, content: { ...base.content, ...patch } });
    this.serialSave(id, () =>
      send(this.base(id))
        .catch((err) => (err.status === 409 ? this.api.getResource(id).then(send) : Promise.reject(err)))
        .then(
          (nr) => this.saved(nr),
          (err) => {
            this.handleErr(err);
            this.refresh();
          },
        ),
    );
  }

  // ---------- агент и MCP (фаза 7.3, ADR-016) ----------

  /** Токены и внешние серверы — с сервера; свежевыпущенный токен виден только до перезагрузки страницы. */
  loadAgent(force) {
    if (!this.live || (!force && (this._agent || this._agentLoading))) return;
    this._agentLoading = true;
    Promise.all([this.api.tokens(), this.api.mcpServers()]).then(
      ([tokens, servers]) => {
        this._agentLoading = false;
        this._agent = { ...(this._agent || {}), tokens, servers };
        this.setState({ agV: Date.now() });
      },
      (e) => {
        this._agentLoading = false;
        this.handleErr(e);
      },
    );
  }

  /**
   * Настройки «Агент и MCP» дизайна на настоящих данных. В дизайне один токен с правами read/write/links:
   * «Перевыпустить» выпускает новый и отзывает прежние. Сервер хранит только хэш — показать токен можно
   * сразу после выпуска, потом — только замаскированный.
   */
  liveAgent(v, s) {
    if (!v.ag || !this.live) return;
    if (s.setTab !== 'agent' && !this._agent) return;
    this.loadAgent();
    const ag = this._agent || { tokens: [], servers: [] };
    const cur = ag.tokens[0] || null;
    const fresh = ag.fresh && cur && ag.fresh.id === cur.id ? ag.fresh.token : null;
    const url = location.origin + '/api/mcp';
    const shown = fresh ? (s.showToken ? fresh : cur.masked) : cur ? cur.masked : 'Токен ещё не выпущен';
    const PERMS = ['read', 'write', 'links'];
    const scopes = cur ? cur.scopes : PERMS.filter((k) => this.state.ui.perms?.[k]);
    const toast = (m) => this.toast(m);
    const err = (e) => this.handleErr(e);
    Object.assign(v.ag, {
      url,
      token: shown,
      copyUrl: () => this.copy(url, 'Адрес скопирован'),
      toggleToken: () =>
        fresh
          ? this.setState({ showToken: !s.showToken })
          : toast(
              cur
                ? 'Токен виден только сразу после выпуска. Перевыпустите, чтобы скопировать'
                : 'Сначала выпустите токен',
            ),
      regen: () =>
        this.api.createToken({ name: 'Агент', scopes }).then(async (t) => {
          await Promise.all(ag.tokens.map((x) => this.api.revokeToken(x.id).catch(() => {})));
          this._agent = { ...ag, tokens: [t], fresh: { id: t.id, token: t.token } };
          this.setState({ showToken: true, agV: Date.now() });
          toast(cur ? 'Токен перевыпущен. Старый больше не работает' : 'Токен выпущен — скопируйте его сейчас');
        }, err),
      snippet: JSON.stringify(
        {
          mcpServers: {
            socrat: { type: 'http', url, headers: { Authorization: 'Bearer ' + (fresh || cur?.masked || '<токен>') } },
          },
        },
        null,
        2,
      ),
    });
    v.ag.perms = (v.ag.perms || []).map((p, i) => {
      const k = PERMS[i];
      const on = scopes.includes(k);
      return {
        ...p,
        bg: on ? 'hsl(var(--primary))' : 'hsl(var(--input))',
        x: on ? '16px' : '2px',
        onClick: () => {
          const next = on ? scopes.filter((x) => x !== k) : [...scopes, k];
          this.setUi({ perms: Object.fromEntries(PERMS.map((x) => [x, next.includes(x)])) });
          if (!cur) return;
          this.api.patchToken(cur.id, { scopes: next }).then((t) => {
            this._agent = { ...this._agent, tokens: [t, ...ag.tokens.slice(1)] };
            this.setState({ agV: Date.now() });
          }, err);
        },
      };
    });
    v.ag.servers = ag.servers.map((m) => {
      const ok = m.enabled && m.status === 'ok';
      const status = !m.enabled
        ? 'Выключен'
        : m.status === 'ok'
          ? `${m.toolsCount} инструментов`
          : m.status === 'error'
            ? 'Ошибка: ' + (m.lastError || 'нет связи')
            : 'Не проверен';
      return {
        name: m.name,
        url: m.url,
        transport: m.transport === 'sse' ? 'SSE' : 'HTTP',
        ini: m.name.slice(0, 2),
        tint: ['#f7701f1f', '#f7701f'],
        status,
        dot: ok
          ? 'hsl(var(--success))'
          : m.status === 'error' && m.enabled
            ? 'hsl(var(--destructive))'
            : 'hsl(var(--muted-foreground) / .5)',
        anim: 'none',
        bg: m.enabled ? 'hsl(var(--primary))' : 'hsl(var(--input))',
        x: m.enabled ? '16px' : '2px',
        onToggle: () =>
          this.api.patchMcpServer(m.id, { enabled: !m.enabled }).then((n) => {
            this._agent = { ...this._agent, servers: this._agent.servers.map((x) => (x.id === n.id ? n : x)) };
            this.setState({ agV: Date.now() });
          }, err),
        onRemove: () =>
          this.api.deleteMcpServer(m.id).then(() => {
            this._agent = { ...this._agent, servers: this._agent.servers.filter((x) => x.id !== m.id) };
            this.setState({ agV: Date.now() });
            toast(`«${m.name}» отключён`);
          }, err),
      };
    });
    v.ag.noServers = !ag.servers.length;
    const f = s.mcpForm || { name: '', url: '', token: '', transport: 'http' };
    v.ag.connect = () => {
      if (!f.name.trim() || !/^https?:\/\/\S+$/.test(f.url.trim()))
        return toast('Укажите название и адрес вида https://…');
      const name = f.name.trim().slice(0, 60);
      this.setState({ mcpForm: { name: '', url: '', token: '', transport: f.transport } });
      toast(`Подключаем «${name}»…`);
      this.api
        .createMcpServer({ name, url: f.url.trim(), transport: f.transport, ...(f.token ? { token: f.token } : {}) })
        .then((n) => {
          this._agent = { ...this._agent, servers: [n, ...(this._agent?.servers || [])] };
          this.setState({ agV: Date.now() });
          toast(n.status === 'ok' ? `«${name}» подключён` : `Не удалось подключить «${name}»: ${n.lastError}`);
        }, err);
    };
  }

  // ---------- AI-связи (фаза 7.2, ADR-015) ----------

  /**
   * Кнопка «AI-связи»: сервер отвечает сразу (202), анализ идёт в воркере, итог — событием `analyze` в потоке
   * проекта. Повторный запуск во время анализа ждёт тот же. Без события за 10 минут — снимаем ожидание.
   */
  runAi() {
    if (!this.live) return super.runAi();
    this.setState({ ai: true });
    const finish = (count, err) => {
      clearTimeout(this._aiJob?.timer);
      this._aiJob = null;
      this.setState({ ai: false });
      if (err) return this.handleErr(err);
      this.toast(count ? `AI предложил связей: ${count}` : 'Новых связей не найдено');
      this.refresh();
    };
    this.api.analyze(this.state.pid).then(
      ({ jobId }) => {
        if (this._aiEarly?.has(jobId)) return finish(this._aiEarly.get(jobId));
        this._aiJob = { jobId, done: (n) => finish(n), timer: setTimeout(() => finish(0), 10 * 60_000) };
      },
      (e) => finish(0, e),
    );
  }

  // ---------- задачи (фаза 6, ADR-013) ----------

  /** Ранг для конца колонки канбана (дробный индекс: вставка без пересчёта соседей). */
  rankAfter(status, exceptId) {
    const ranks = (this.state.snap?.resources ?? [])
      .filter((t) => t.kind === 'task' && t.id !== exceptId && t.content.status === status && t.content.rank)
      .map((t) => t.content.rank)
      .sort();
    try {
      return generateKeyBetween(ranks.at(-1) ?? null, null);
    } catch {
      return generateKeyBetween(null, null); // чужой некорректный ранг — не ломаем добавление
    }
  }

  /** Новая задача — в конец своей колонки (прототип создаёт без ранга). */
  create(kind, extra = {}) {
    if (kind === 'task' && !FEAT.tasks) return;
    if (!this.live || kind !== 'task' || extra.tpl) return super.create(kind, extra);
    this.setState({ newMenu: false, dd: null });
    const status = extra.status || 'todo';
    const content = { text: '', status, priority: 'medium', assignee: '', dueDate: '', rank: this.rankAfter(status) };
    this.api
      .createResource(this.state.pid, { kind: 'task', title: 'Новая задача', content })
      .then(({ id }) => this.refresh().then(() => this.setState({ view: 'tasks', openId: null, selId: id })))
      .catch((e) => this.handleErr(e));
  }

  /**
   * Порядок задач в снимке — по рангу (задачи без ранга — первыми, по времени создания): канбан прототипа
   * берёт задачи в порядке снимка. Сортируем на месте только места задач, остальные материалы не двигаются.
   */
  sortTasks() {
    const rs = this.state.snap?.resources;
    if (!rs || this._sortedSnap === rs) return;
    const slots = [];
    const tasks = [];
    rs.forEach((r, i) => r.kind === 'task' && (slots.push(i), tasks.push(r)));
    const key = (t) => [t.content.rank || '', t.created_at || ''];
    tasks.sort((a, b) => {
      const [ra, ca] = key(a);
      const [rb, cb] = key(b);
      return ra < rb ? -1 : ra > rb ? 1 : ca < cb ? -1 : ca > cb ? 1 : 0;
    });
    slots.forEach((i, j) => (rs[i] = tasks[j]));
    this._sortedSnap = rs;
  }

  /** Канбан: просрочка по сегодняшней дате (в прототипе дата зашита). */
  liveTasks(v) {
    if (!v.isTasks || !Array.isArray(v.columns)) return;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const tasks = this.state.snap.resources.filter((r) => r.kind === 'task');
    ['todo', 'doing', 'done'].forEach((k, i) => {
      const list = tasks.filter((t) => t.content.status === k);
      (v.columns[i]?.cards ?? []).forEach((c, j) => {
        const due = list[j]?.content.dueDate;
        c.dueColor = due && due < today && k !== 'done' ? 'hsl(var(--destructive))' : 'hsl(var(--muted-foreground))';
      });
    });
  }

  // ---------- совместное редактирование (ADR-011) ----------
  // Документы, карты и доски — через Yjs и collab: правки сливаются по символам, у всех сразу.
  // REST остаётся для метаданных (название, свойства страницы). Пока сессия не синхронизирована — только чтение.

  _collab = null;
  _prevGraph = {}; // id → последнее показанное или отправленное содержимое карты (формат API) — основа для разницы
  _versions = {}; // id → { at, list }
  _graphInit = {};

  get collab() {
    if (!this.live || !this._authed) return null;
    if (!this._collab) {
      this._collab = new CollabHub({
        change: (id) => this.collabChange(id),
        remote: (id) => this.collabRemote(id),
        stateless: (id, msg) => this.collabMessage(id, msg),
      });
      if (this._me) this._collab.setUser(this._me.id, this._me.name);
    }
    return this._collab;
  }

  collabSession(id) {
    return this._collab?.sessions.get(id) ?? null;
  }

  /** Тип материала для совместной работы (документ, карта или доска). */
  collabKind(r) {
    return r && (r.kind === 'note' || r.kind === 'mindmap') ? r.kind : null;
  }

  /** Открыть сессию открытого материала, остальные — отпустить (после рендера, не во время). */
  syncCollab() {
    const hub = this.collab;
    if (!hub) return;
    const r = this.state.openId ? this.res(this.state.openId) : null;
    const id = this.collabKind(r) ? r.id : null;
    if (id && !hub.sessions.has(id)) {
      hub.open(id);
      // не подключились за 6 с — показать содержимое из снимка только для чтения и сказать об этом
      clearTimeout(this._collabSlow);
      this._collabSlow = setTimeout(() => {
        const s = this.collabSession(id);
        if (s && !s.synced && this.state.openId === id) {
          this.toast('Нет связи с сервером совместной работы — файл открыт только для чтения');
          this.setState({ collabV: Date.now() });
        }
      }, 6000);
    }
    for (const sid of hub.sessions.keys()) if (sid !== id) hub.release(sid);
  }

  collabChange(id) {
    const s = this.collabSession(id);
    if (!s) return;
    if (s.synced && !this._graphInit[id] && this.res(id)?.kind === 'mindmap') {
      this._graphInit[id] = true;
      this.collabRemote(id);
    }
    if (s.denied) this.toast('Нет доступа к файлу');
    this.setState({ collabV: Date.now() });
  }

  /** Карта или доска изменилась у других (или отменили своё) — показать состояние Yjs. */
  collabRemote(id) {
    const s = this.collabSession(id),
      r = this.res(id);
    if (!s || !r || r.kind !== 'mindmap') return;
    const graph = yToGraph(s.doc);
    this._prevGraph[id] = graph;
    const kind = kindOut('mindmap', r.content);
    this.setState((st) => ({ maps: { ...st.maps, [id]: contentIn(kind, graph) } }));
  }

  collabMessage(id, msg) {
    if (msg.type === 'restored') {
      delete this._versions[id];
      this.toast('Версия восстановлена');
      this.setState({ collabV: Date.now() });
    } else if (msg.type === 'error') this.toast(msg.code === 'forbidden' ? 'Недостаточно прав' : 'Версия не найдена');
  }

  /** Серверные версии для вкладки «История» (формат островка: id, at, title, blocks, words, who). */
  versionsFor(id) {
    const cached = this._versions[id];
    if (!cached || (Date.now() - cached.at > 60_000 && !cached.loading)) {
      const entry = (this._versions[id] = { at: Date.now(), list: cached?.list ?? [], loading: true });
      this.api
        .versions(id)
        .then(async (list) => {
          const top = list.slice(0, 20);
          const contents = await Promise.all(top.map((v) => this.api.version(id, v.id).catch(() => null)));
          entry.list = top.map((v, i) => {
            const blocks = contents[i]?.content?.blocks ?? [];
            return {
              id: v.id,
              at: Date.parse(v.createdAt),
              title: v.title,
              blocks,
              words: blocksToText(blocks).split(/\s+/).filter(Boolean).length,
              who:
                v.authors.map((a) => a.name).join(', ') ||
                (v.reason === 'restore' ? 'До восстановления' : 'Автосохранение'),
            };
          });
        })
        .catch(() => {})
        .finally(() => {
          entry.loading = false;
          this.setState({ collabV: Date.now() });
        });
    }
    const r = this.res(id);
    const blocks = r?.content?.blocks ?? [];
    const current = {
      id: 'current',
      at: Date.now(),
      title: r?.title ?? '',
      blocks,
      words: blocksToText(blocks).split(/\s+/).filter(Boolean).length,
      who: 'Сейчас',
    };
    return [current, ...(this._versions[id]?.list ?? [])];
  }

  /** Подключить совместное редактирование к островкам (документ, карта, доска) — после renderVals прототипа. */
  liveCollab() {
    const hub = this._collab;
    const doc = this.islandProps.doc;
    if (doc && hub) {
      const s = hub.sessions.get(doc.docId);
      const slow = s && !s.synced && Date.now() - (s.openedAt ??= Date.now()) > 6000;
      if (s?.synced) {
        const me = hub.user;
        doc.key = doc.docId + '#collab#' + (this._docRev[doc.docId] ?? 0);
        doc.collab = {
          provider: s.provider,
          fragment: s.doc.getXmlFragment(YDOC_BLOCKS),
          user: me ? { name: me.name, color: me.color } : undefined,
        };
        doc.authors = [
          { ini: 'Вы', name: 'Вы' },
          ...s.peers.map((p) => ({ ini: ini(p.name), name: p.name, bg: p.color + '33', fg: p.color })),
        ];
        doc.versions = this.versionsFor(doc.docId);
        doc.onRestoreVersion = (vid) => s.provider.sendStateless(JSON.stringify({ type: 'restore', versionId: vid }));
      } else {
        // ждём синхронизации: пустой редактор только для чтения (без старого содержимого и без записи)
        doc.key = doc.docId + (slow ? '#readonly' : '#wait');
        doc.canEdit = false;
        if (!slow) Object.assign(doc, { blocks: [], text: '' });
      }
    }
    for (const key of ['mind', 'board']) {
      const p = this.islandProps[key];
      const id = p?.mapId;
      if (!p || !id || !hub) continue;
      const s = hub.sessions.get(id);
      if (!s?.synced) p.canEdit = false; // правки до синхронизации потерялись бы при загрузке состояния
    }
  }

  /** Свойства документа (название, обложка…) — REST с версией; при 409 — поверх свежей версии (последняя побеждает). */
  saveDocMeta(id, pl) {
    const r = this.res(id);
    if (!r) return;
    const title = ((pl.title || '').trim() || 'Без названия').slice(0, 160);
    const page = pl.page ?? r.content.page;
    if (title === r.title && JSON.stringify(page ?? null) === JSON.stringify(r.content.page ?? null)) return;
    const send = (base) =>
      this.api.patchResource(id, { version: base.version, title, content: { ...base.content, page } });
    this.serialSave(id, () =>
      send(this.res(id))
        .catch((err) => (err.status === 409 ? this.api.getResource(id).then(send) : Promise.reject(err)))
        .then(
          (nr) => this.replaceRes(nr),
          (err) => this.handleErr(err),
        ),
    );
  }

  /** Отложенное сохранение прототипа: при совместной работе — сразу (Yjs сам доставит правку). */
  queueSave(id, fn) {
    if (this.collabSession(id)?.synced) return fn.call(this, id);
    return super.queueSave(id, fn);
  }

  /** Отмена и повтор на карте: при совместной работе — только свои действия (Y.UndoManager), чужие не откатываются. */
  histStep(dir) {
    const id = this.state.openId,
      s = this.collabSession(id);
    if (!s?.synced || !s.undo) return super.histStep(dir);
    if (!this.canEdit) return;
    const can = dir < 0 ? s.undo.canUndo() : s.undo.canRedo();
    if (!can) return this.toast(dir < 0 ? 'Нечего отменять' : 'Нечего повторять');
    if (dir < 0) s.undo.undo();
    else s.undo.redo();
    this.collabRemote(id);
    this.toast(dir < 0 ? 'Действие отменено · ⌘⇧Z — вернуть' : 'Действие повторено');
  }

  /** Положения узлов считает автораскладка (майнд-карта не в свободном режиме) — при слиянии их не сравниваем. */
  mapLayout(c) {
    return c?.style?.kind !== 'board' && c?.style?.structure !== 'free';
  }

  async mapConflict(id, base) {
    const c = (this.conflicts[id] = { kind: 'map', base, mine: null, theirs: null, items: [] });
    this.setState((s) => ({ save: { ...s.save, [id]: 'conflict' } }));
    try {
      c.theirs = await this.api.getResource(id);
    } catch (e) {
      delete this.conflicts[id];
      return this.handleErr(e);
    }
    c.mine = this.mapContent(id);
    const m = mergeGraph(base.content, c.mine, c.theirs.content, { layout: this.mapLayout(c.mine) });
    if (!m.conflicts.length) return this.resolveConflict(id, 'merge');
    c.items = m.conflicts;
    overlay.openDialog('conflict', id);
  }

  /** choice: merge — объединить (без пересечений), mine — оставить мою версию, theirs — взять серверную. */
  async resolveConflict(id, choice) {
    const c = this.conflicts[id];
    if (!c?.theirs) return;
    const latest = c.theirs;
    let patch = null;
    if (c.kind === 'doc') {
      if (choice === 'mine') patch = this.docPatch(latest, c.mine);
      else if (choice === 'merge') {
        const m = this.mergeDocNow(c);
        patch = this.docPatch(latest, { title: m.result.title, blocks: m.result.blocks, page: m.result.page });
      }
    } else {
      const mine = this.mapContent(id); // с правками, сделанными за время конфликта
      if (choice === 'mine') patch = { content: mine };
      else if (choice === 'merge')
        patch = { content: mergeGraph(c.base.content, mine, latest.content, { layout: this.mapLayout(mine) }).result };
    }
    try {
      const nr = patch ? await this.api.patchResource(id, { version: latest.version, ...patch }) : latest;
      delete this.conflicts[id];
      overlay.close();
      if (c.kind === 'map') this.setState((s) => ({ maps: { ...s.maps, [id]: undefined } }));
      // документ пересоздаём с новым содержимым (кроме «моей» версии — она и так в редакторе)
      if (c.kind === 'doc' && choice !== 'mine') this._docRev[id] = (this._docRev[id] ?? 0) + 1;
      this.saved(nr);
      overlay.snack(
        choice === 'merge'
          ? 'Подтянули правки другого участника — ваши тоже сохранены'
          : choice === 'mine'
            ? 'Сохранена ваша версия'
            : 'Открыта версия другого участника',
      );
    } catch (err) {
      if (err.status === 409) {
        // пока решали, файл снова изменили — сравниваем с новой версией
        delete this.conflicts[id];
        overlay.close();
        return c.kind === 'doc' ? this.docConflict(id, c.base, c.mine) : this.mapConflict(id, c.base);
      }
      overlay.snack(err.message);
    }
  }

  // ---------- обсуждения и «Входящие» (фаза 5, ADR-012) ----------

  _cmLive = null; // { pid, data: { [resourceId]: ветки в формате прототипа } }
  _cmLoading = null;
  _cmAgain = false;
  _ib = null; // { items } — последние уведомления с сервера
  _ibLoading = false;

  /** Ветки проекта с сервера в формате прототипа ({ id, a, t, at, rep, res }); грузятся при первом обращении. */
  cmData() {
    if (!this.live) return super.cmData();
    if (this._cmLive?.pid !== this.state.pid) {
      this.loadComments();
      return {};
    }
    return this._cmLive.data;
  }

  cmSave(all) {
    if (!this.live) return super.cmSave(all);
    this._cmLive = { ...this._cmLive, data: all };
    this.setState({ cmV: Date.now() });
  }

  loadComments(force) {
    const pid = this.state.pid;
    if (!this.live || !this._authed || !/^[0-9a-f-]{36}$/i.test(pid || '')) return;
    if (this._cmLoading) {
      if (force || this._cmLoading !== pid) this._cmAgain = true;
      return;
    }
    if (!force && this._cmLive?.pid === pid) return;
    this._cmLoading = pid;
    const done = (data) => {
      this._cmLoading = null;
      if (this.state.pid === pid) {
        this._cmLive = { pid, data };
        this.setState({ cmV: Date.now() });
      }
      if (this._cmAgain) {
        this._cmAgain = false;
        this.loadComments(true);
      }
    };
    this.api.projectComments(pid).then(
      (list) => {
        const data = {};
        for (const t of list) (data[t.resourceId] ||= []).push(this.cmFromServer(t));
        done(data);
      },
      () => done(this._cmLive?.pid === pid ? this._cmLive.data : {}), // повторим по следующему событию
    );
  }

  /** Автор для прототипа: [инициалы, имя, фон, цвет] — цвета как в списке участников. */
  cmPerson(p) {
    if (!p) return ['?', 'Бывший участник', 'hsl(var(--muted))', 'hsl(var(--muted-foreground))'];
    if (p.id === this._me?.id) return this.cmMe();
    const i = (this.state.memData?.[this.state.sid]?.list || []).findIndex((m) => m.userId === p.id);
    const [bg, fg] = AVATAR[(i < 0 ? [...p.id].reduce((a, c) => a + c.charCodeAt(0), 0) : i) % AVATAR.length];
    return [ini(p.name), p.name, bg, fg];
  }

  cmFromServer(t) {
    return {
      id: t.id,
      a: this.cmPerson(t.author),
      t: t.deleted ? 'Комментарий удалён' : t.body,
      at: Date.parse(t.createdAt),
      res: !!t.resolved,
      rep: t.replies.map((r) => ({ id: r.id, a: this.cmPerson(r.author), t: r.body, at: Date.parse(r.createdAt) })),
    };
  }

  /** Ветка с сервера — на место старой (или новой в конец). */
  cmPut(t) {
    const all = { ...this.cmData() };
    const list = all[t.resourceId] || [];
    const next = this.cmFromServer(t);
    all[t.resourceId] = list.some((x) => x.id === t.id) ? list.map((x) => (x.id === t.id ? next : x)) : [...list, next];
    this.cmSave(all);
  }

  /** Участники спейса для упоминаний `@Имя` (кроме себя). */
  cmPeople() {
    return (this.state.memData?.[this.state.sid]?.list || [])
      .filter((m) => m.userId !== this._me?.id)
      .map((m) => ({ id: m.userId, name: m.name }));
  }

  /** Новая ветка или ответ: упоминания — по именам участников; что не дошло (нет доступа) — говорим. */
  cmSend(fid, body, parentId = null) {
    const people = this.cmPeople();
    const mentions = findMentions(body, people);
    const name = (id) => people.find((p) => p.id === id)?.name;
    return this.api.addComment(fid, { body, parentId, mentions }).then(
      (t) => {
        this.cmPut(t);
        const got = (parentId ? t.replies.at(-1)?.mentions : t.mentions) || [];
        const lost = mentions.filter((id) => !got.includes(id));
        if (lost.length)
          this.toast('Нет доступа к файлу: ' + lost.map(name).join(', ') + ' — упоминание не отправлено');
        else if (got.length) this.toast('Упоминание отправлено: ' + got.map(name).join(', '));
      },
      (e) => {
        this.toast(e.message || 'Не удалось отправить комментарий');
        this.loadComments(true); // убрать оптимистичный ответ
      },
    );
  }

  cmAdd(fid, c) {
    if (!this.live) return super.cmAdd(fid, c);
    this.cmSend(fid, c.t);
  }

  /** Прототип меняет ветку функцией: из разницы понимаем, что сделали — решили/открыли или ответили. */
  cmEdit(fid, cid, fn) {
    if (!this.live) return super.cmEdit(fid, cid, fn);
    const old = (this.cmData()[fid] || []).find((c) => c.id === cid);
    if (!old) return;
    const next = fn({ ...old });
    super.cmEdit(fid, cid, () => next); // сразу на экране; ответ сервера заменит
    if (!!next.res !== !!old.res)
      this.api.patchComment(cid, { resolved: !!next.res }).then(
        (t) => this.cmPut(t),
        (e) => {
          this.toast(e.status === 403 ? 'Решить ветку может её автор или редактор файла' : e.message);
          this.loadComments(true);
        },
      );
    else if ((next.rep || []).length > (old.rep || []).length) this.cmSend(fid, next.rep.at(-1).t, cid);
  }

  liveComments(v, s) {
    if (!v.cm) return;
    if (s.mwOpen) this.loadMembers(s.sid); // имена для упоминаний и цвета авторов
    if (!v.cm.canWrite || !s.openId) return;
    const send = () => {
      const t = (s.cmText || '').trim();
      if (!t) return;
      this.cmSend(s.openId, t.slice(0, 4000));
      this.setState({ cmText: '' });
    };
    v.cm.send = send;
    v.cm.onKey = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    };
  }

  /**
   * Поток событий (SSE): счётчик непрочитанных и «обсуждения изменились» для открытого проекта.
   * Переподключаемся при смене проекта; EventSource сам восстанавливает соединение после обрыва.
   */
  syncEvents() {
    const pid = this.state.pid;
    const key = this.live && this._authed ? (/^[0-9a-f-]{36}$/i.test(pid || '') ? pid : '') : null;
    if (key === this._evKey) return;
    this._ev?.close();
    this._ev = null;
    this._evKey = key;
    if (key === null || typeof EventSource === 'undefined') return;
    const es = new EventSource('/api/events' + (key ? '?project=' + key : ''));
    es.addEventListener('unread', (e) => {
      const { count } = JSON.parse(e.data);
      if (count === this.state.ibUnread) return;
      this.setState({ ibUnread: count });
      if (this._ib) this.loadInbox();
    });
    es.addEventListener('comments', () => {
      clearTimeout(this._cmTimer);
      this._cmTimer = setTimeout(() => this.loadComments(true), 200);
    });
    // задачи (у соавтора, из чек-листа документа) или AI-предложения связей изменились — перечитать снимок;
    // свою правку ещё сохраняем — позже
    const refreshSoon = () => {
      clearTimeout(this._tkTimer);
      const again = () => {
        const busy = Object.values(this._saving || {}).some(Boolean);
        this._tkTimer = setTimeout(() => (busy ? again() : this.refresh()), busy ? 500 : 300);
      };
      again();
    };
    es.addEventListener('tasks', refreshSoon);
    es.addEventListener('links', refreshSoon);
    es.addEventListener('analyze', (e) => {
      const p = JSON.parse(e.data);
      if (!p.finished) return;
      if (this._aiJob?.jobId === p.jobId) this._aiJob.done(p.count ?? 0);
      else (this._aiEarly ||= new Map()).set(p.jobId, p.count ?? 0); // маленький проект успел раньше ответа на POST
    });
    this._ev = es;
  }

  loadInbox() {
    if (this._ibLoading) {
      this._ibAgain = true;
      return;
    }
    this._ibLoading = true;
    this.api.notifications().then(
      (r) => {
        this._ib = { items: r.items };
        this._ibLoading = false;
        this.setState({ ibUnread: r.unread, ibV: Date.now() });
        if (this._ibAgain) {
          this._ibAgain = false;
          this.loadInbox();
        }
      },
      (e) => {
        this._ibLoading = false;
        this._ib ||= { items: [] };
        this.handleErr(e);
      },
    );
  }

  /** «Входящие» дизайна на настоящих уведомлениях: упоминания, ответы в ветках, принятые приглашения. */
  liveInbox(v, s) {
    if (!v.ib) return;
    v.ib.count = s.ibUnread || false;
    if (s.view !== 'inbox' && !this._ib) {
      Object.assign(v.ib, { items: [], empty: true }); // демо-уведомления прототипа не показываем
      return;
    }
    if (!this._ib) {
      this.loadInbox();
      Object.assign(v.ib, { items: [], empty: false });
      return;
    }
    const tab = s.ibTab || 'all';
    const list = this._ib.items.filter((n) =>
      tab === 'unread' ? !n.readAt : tab === 'mentions' ? n.kind === 'mention' || n.kind === 'comment' : true,
    );
    const KIND = {
      mention: ['at-sign', '#3e63dd'],
      comment: ['message-square', '#8e4ec6'],
      invite: ['user-plus', '#0d9488'],
      assign: ['square-check-big', '#30a46c'],
      due: ['alarm-clock', '#f7701f'],
    };
    const read = (ids) => {
      const set = new Set(ids);
      const now = new Date().toISOString();
      this._ib = { items: this._ib.items.map((n) => (set.has(n.id) && !n.readAt ? { ...n, readAt: now } : n)) };
      this.setState({ ibUnread: Math.max(0, (s.ibUnread || 0) - ids.length), ibV: Date.now() });
    };
    v.ib.readAll = () => {
      const ids = this._ib.items.filter((n) => !n.readAt).map((n) => n.id);
      read(ids);
      this.api.readNotifications({ all: true }).then(
        (r) => this.setState({ ibUnread: r.unread }),
        (e) => this.handleErr(e),
      );
    };
    v.ib.empty = !list.length;
    v.ib.items = list.map((n) => {
      const [bg, fg] = n.actor ? this.cmPerson(n.actor).slice(2) : ['#f7701f1f', '#f7701f'];
      const k = KIND[n.kind] || KIND.mention;
      return {
        who: notificationWho(n.kind, n.actor?.name),
        text: notificationAction(n.kind, !!n.commentId),
        quote: n.quote,
        where: n.unavailable ? 'Нет доступа' : n.resource?.title || n.projectName || n.spaceName,
        time: agoText(Date.parse(n.createdAt)),
        ini: n.actor ? ini(n.actor.name) : n.kind === 'due' ? 'S' : '?',
        avBg: bg,
        avFg: fg,
        icon: IC(k[0]),
        kindBg: k[1],
        unread: !n.readAt,
        bg: n.readAt ? 'transparent' : 'hsl(var(--primary) / .04)',
        onClick: () => {
          if (!n.readAt) {
            read([n.id]);
            this.api.readNotifications({ ids: [n.id] }).catch(() => {});
          }
          if (n.resourceId) this.openLink(n.projectId, n.resourceId, n.commentId);
        },
      };
    });
  }

  /** Открыть материал (и панель обсуждения) в любом доступном проекте: из «Входящих» и ссылок из писем. */
  openLink(pid, rid, cid) {
    const openIt = () => {
      if (!this.state.snap?.resources.some((x) => x.id === rid)) return this.toast('Файл недоступен или удалён');
      this.open(rid);
      if (cid) this.setState({ mwOpen: true, mwPeople: false, inviting: false });
    };
    if (!pid || pid === this.state.pid) return openIt();
    this.switchProject(pid);
    const t0 = Date.now();
    const wait = () =>
      this.state.snap?.project?.id === pid ? openIt() : Date.now() - t0 < 8000 ? setTimeout(wait, 150) : null;
    wait();
  }

  /** Экраны задач и KPI — только с задачами; вкладки агента и подписки — только с этими функциями (ADR-017). */
  go(view) {
    if ((view === 'tasks' || view === 'kpi') && !FEAT.tasks) view = 'home';
    return super.go(view);
  }

  /**
   * Функции в разработке (ADR-017) — скрыты в релизе: в шаблоне прототипа они есть, здесь их убираем из значений.
   * Безусловные блоки шаблона (сайты, отделы, значок подписки) обёрнуты условием v.feat в scripts/sync-legacy.py.
   */
  hideDev(v, s) {
    v.feat = { ...FEAT };
    const ip = this.islandProps;
    if (!FEAT.tasks) {
      Object.assign(v, { isTasks: false, showTask: false, isKpi: false });
      const noTask = (t) => t.title !== 'Карточка задачи (K)';
      if (Array.isArray(v.boardTools)) v.boardTools = v.boardTools.filter(noTask);
      if (Array.isArray(v.gut?.items)) v.gut.items = v.gut.items.filter(noTask);
      if (ip.doc) ip.doc.metrics = [];
    }
    if (!FEAT.ai) {
      if (v.pv) Object.assign(v.pv, { hasAi: false, ai: [] });
      if (ip.doc) ip.doc.ai = [];
      if (ip.canvas?.edges) ip.canvas.edges = ip.canvas.edges.filter((e) => !e.ai);
      if (Array.isArray(v.pf?.notifs)) v.pf.notifs = v.pf.notifs.filter((t) => t.label !== 'Предложения AI');
    }
    const hiddenTabs = [!FEAT.agent && 'Агент и MCP', !FEAT.billing && 'Подписка и оплата'].filter(Boolean);
    if (hiddenTabs.length) {
      if (Array.isArray(v.setTabs)) v.setTabs = v.setTabs.filter((t) => !hiddenTabs.includes(t.label));
      if (Array.isArray(v.pill?.items)) v.pill.items = v.pill.items.filter((b) => !hiddenTabs.includes(b.title));
      if ((s.setTab === 'agent' && !FEAT.agent) || (s.setTab === 'billing' && !FEAT.billing))
        Object.assign(v, { stAgent: false, stBilling: false, stFonts: false, stProfile: true });
    }
    if (!FEAT.billing) {
      if (v.sx) {
        if (Array.isArray(v.sx.tabs)) v.sx.tabs = v.sx.tabs.filter((t) => t.label !== 'Тариф');
        if (v.sx.tPlan) Object.assign(v.sx, { tPlan: false, tGeneral: true, tabTitle: 'Общее' });
        v.sx.planLabel = '';
      }
      // «Free · участник» → «Участник»
      const noPlan = (x) => {
        if (typeof x?.sub === 'string') x.sub = x.sub.replace(/^(Free|Pro|Team) · (.)/, (_, _p, c) => c.toUpperCase());
      };
      [...(v.ss?.items ?? []), v.ss?.cur, ...(v.spaces ?? [])].forEach(noPlan);
    }
    if (!FEAT.sites) {
      if (Array.isArray(v.np?.types)) v.np.types = v.np.types.filter((o) => o.label === 'Проект');
      if (Array.isArray(v.sx?.acc)) v.sx.acc = v.sx.acc.filter((t) => t.label !== 'Публикация сайтов');
      if (Array.isArray(v.au?.cr)) v.au.cr = v.au.cr.filter((c) => !/айт/.test(c.label));
    }
  }

  renderVals() {
    if (this.live) {
      // исполнители задач, авторы и т. п. в прототипе — из MEMBERS: подставляем участников текущего спейса
      const list = this.state.memData?.[this.state.sid]?.list ?? [];
      setMembers(Object.fromEntries(list.map((m) => [m.userId, { name: m.name, ini: ini(m.name) }])));
      this.sortTasks();
    }
    const v = super.renderVals();
    if (!this.live) {
      v.feat = { tasks: true, ai: true, agent: true, billing: true, sites: true }; // демо прототипа (?mock=1) — всё
      return v;
    }
    const s = this.state;
    this.liveSpaceModal(v, s);
    this.liveNewSpace(v, s);
    this.liveAccount(v);
    this.decorateRows(v);
    const doc = this.islandProps.doc;
    if (doc) {
      const id = doc.docId;
      doc.key = id + '#' + (this._docRev[id] ?? 0);
      if (this._docOpen !== doc.key) {
        this._docOpen = doc.key; // редактор создаётся заново — его содержимое соответствует текущей версии
        const r = this.res(id),
          b = this._base[id];
        this._base[id] = b && b.version > r.version ? b : r; // своё сохранение могло ещё не попасть в снимок
      }
      if (this.conflicts[id]) doc.canEdit = false;
      doc.authors = []; // «сейчас в документе» — только вы, пока нет присутствия (фаза 4)
    } else this._docOpen = null;
    if (this.islandProps.board) this.islandProps.board.demoPeers = false;
    // Прототип: у скрытой панели участников pointer-events: none, но у вложенных списка и приглашения — auto,
    // и невидимый список перехватывает клики по «Обсуждению» (поле ввода, «Ответить», «Решить»).
    if (v.mw && v.mw.pplPe === 'none') Object.assign(v.mw, { listPe: 'none', invPe: 'none' });
    this.liveComments(v, s);
    this.liveInbox(v, s);
    this.liveTasks(v);
    this.liveAgent(v, s);
    this.liveCollab();
    // Подписи спейсов «Free · роль»: в прототипе нет модератора и гостя — без этого видно «Free · viewer»
    const roleSub = (x) => {
      const m = typeof x?.sub === 'string' && /^(Free|Team) · (moderator|viewer)$/.exec(x.sub);
      if (m) x.sub = m[1] + ' · ' + ROLE_LABEL[m[2]].toLowerCase();
    };
    [...(v.ss?.items ?? []), v.ss?.cur, ...(v.spaces ?? [])].forEach(roleSub);
    this.hideDev(v, s);
    v.pvShare = () => {
      const id = s.selId;
      this.copy(
        location.origin + '/?p=' + s.pid + (id ? '&r=' + id : ''),
        'Ссылка скопирована. Откроется у тех, у кого есть доступ',
      );
    };
    queueMicrotask(() => this.syncUrl());
    const soon = (what) => () => this.toast(what + ' появятся в следующих версиях');
    v.newSite = soon('Сайты');
    v.newDept = soon('Отделы');
    if (v.au?.isOnb) {
      v.au.toggleSite = () => this.toast('Сайт спейса появится в следующих версиях');
    }
    if (v.au?.isSent) {
      v.au.resend = () =>
        this.api.resetRequest((this.state.auEmail || '').trim()).then(
          () => this.toast('Письмо отправлено ещё раз'),
          (e) => this.authError(e),
        );
    }
    return v;
  }
}
